import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { App, Card } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import Layout from 'components/Layout/Layout';

import RouteReportBlockedState from 'scenes/RouteReport/components/RouteReportBlockedState';
import { useRouteReportScope } from 'scenes/RouteReport/hooks/useRouteReportScope';
import { useRouteReportTheme } from 'scenes/RouteReport/hooks/useRouteReportTheme';
import { getUserTimeZone } from 'scenes/RouteReport/utils/time';
import 'scenes/VisitHistory/VisitHistory.scss';
import VisitHistoryFilters from 'scenes/VisitHistory/components/VisitHistoryFilters';
import VisitHistoryHeader from 'scenes/VisitHistory/components/VisitHistoryHeader';
import {
  VisitHistoryEmptyRange,
  VisitHistoryErrorState,
} from 'scenes/VisitHistory/components/VisitHistoryStates';
import VisitsByDayChart from 'scenes/VisitHistory/components/VisitsByDayChart';
import { useVisitHistory } from 'scenes/VisitHistory/hooks/useVisitHistory';
import { buildDayPoints } from 'scenes/VisitHistory/utils/charts';
import {
  VisitHistoryFilterState,
  isRangeTooLong,
  narrowDistributors,
  parseVisitHistoryQuery,
  pruneDistributorIds,
  pruneZoneIds,
  serializeVisitHistoryQuery,
  zoneOptions,
} from 'scenes/VisitHistory/utils/filters';
import { formatDecimal } from 'scenes/VisitHistory/utils/format';
import { dayHeaderLabel } from 'scenes/VisitHistory/utils/mapData';
import { enumerateDays } from 'scenes/VisitHistory/utils/range';
import { aggregateStops } from 'scenes/VisitHistory/utils/selection';
import { selectionScope } from 'scenes/VisitHistory/utils/selectionScope';
import { visitPaletteVars } from 'scenes/VisitHistory/utils/stateColors';
import { ERROR_TOAST_SECONDS } from 'scenes/VisitHistory/utils/toast';
import { workbookToBlob } from 'scenes/VisitHistory/utils/xlsxExport';

import DayRoutesModal from './components/DayRoutesModal';
import StoreVisitsCommunes from './components/StoreVisitsCommunes';
import StoreVisitsCompetition from './components/StoreVisitsCompetition';
import StoreVisitsDaySummary from './components/StoreVisitsDaySummary';
import StoreVisitsIncidents from './components/StoreVisitsIncidents';
import StoreVisitsKpis from './components/StoreVisitsKpis';
import StoreVisitsMap, { RouteFocus } from './components/StoreVisitsMap';
import StoreVisitsMaterials from './components/StoreVisitsMaterials';
import { useStoreVisitInsights } from './hooks/useStoreVisitInsights';
import {
  bestAndWorstDay,
  buildCommuneSummary,
  buildDaySummaries,
  buildKpis,
  buildMaterialSummary,
  storeStops,
} from './utils/storeVisits';
import {
  buildStoreVisitsWorkbook,
  storeVisitsFileName,
} from './utils/xlsxExport';

import './StoreVisits.scss';

const I18N_BASE = 'scenes.store_visits';
const REVOKE_DELAY_MS = 1000;

function StoreVisits() {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const { message } = App.useApp();
  const scope = useRouteReportScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const userTimeZone = useMemo(() => getUserTimeZone(), []);
  const themeVars = useRouteReportTheme();

  const filters = useMemo(
    () => parseVisitHistoryQuery(searchParams, dayjs(), userTimeZone),
    [searchParams, userTimeZone]
  );

  useEffect(() => {
    if (!isRangeTooLong(searchParams)) return;
    setSearchParams(serializeVisitHistoryQuery(filters), { replace: true });
    message.warning({
      key: 'store-visits-range',
      content: t('scenes.visit_history.filters.range_clamped', {
        from: dayjs(filters.range.from).format('DD/MM/YYYY'),
        to: dayjs(filters.range.to).format('DD/MM/YYYY'),
      }),
    });
  }, [searchParams, filters, setSearchParams, message, t]);

  const [mapZoneId, setMapZoneId] = useState<string | null>(null);
  const [focusDay, setFocusDay] = useState<string | null>(null);
  const [focusRoute, setFocusRoute] = useState<RouteFocus | null>(null);
  const [openDay, setOpenDay] = useState<string | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);

  const updateFilters = useCallback(
    (next: VisitHistoryFilterState) => {
      setFocusDay(null);
      setFocusRoute(null);
      setMapZoneId(null);
      setSearchParams(serializeVisitHistoryQuery(next), { replace: true });
    },
    [setSearchParams]
  );

  const zones = useMemo(
    () =>
      zoneOptions(scope.distributors, t('scenes.visit_history.zone_unnamed')),
    [scope.distributors, t]
  );
  const zoneIds = useMemo(
    () => pruneZoneIds(scope.distributors, filters.zoneIds),
    [scope.distributors, filters.zoneIds]
  );
  const distributors = useMemo(
    () => narrowDistributors(scope.distributors, zoneIds),
    [scope.distributors, zoneIds]
  );
  const distributorIds = useMemo(
    () =>
      scope.singleDistributor
        ? scope.distributors.map((d) => d.id)
        : pruneDistributorIds(
            scope.distributors,
            zoneIds,
            filters.distributorIds
          ),
    [scope.singleDistributor, scope.distributors, zoneIds, filters]
  );

  const query = useMemo(
    () =>
      scope.loading || scope.blockedReason
        ? undefined
        : {
            from: filters.range.from,
            to: filters.range.to,
            zoneIds,
            distributorIds,
          },
    [scope.loading, scope.blockedReason, filters, zoneIds, distributorIds]
  );

  const { loading, refreshing, summary, stops, lastUpdatedAt, error, refresh } =
    useVisitHistory(query);
  const insightsState = useStoreVisitInsights(query);
  const { insights } = insightsState;

  const stores = useMemo(() => storeStops(stops), [stops]);
  const kpis = useMemo(
    () => (summary ? buildKpis(stores) : null),
    [summary, stores]
  );
  const materials = useMemo(() => buildMaterialSummary(stores), [stores]);
  const communes = useMemo(() => buildCommuneSummary(stores), [stores]);
  const days = useMemo(() => buildDaySummaries(stores), [stores]);
  const bestWorst = useMemo(() => bestAndWorstDay(days), [days]);
  const dayPoints = useMemo(
    () =>
      buildDayPoints(
        enumerateDays(filters.range.from, filters.range.to),
        aggregateStops(stores, userTimeZone).byDay
      ),
    [filters.range, stores, userTimeZone]
  );
  const dimScope = useMemo(
    () =>
      selectionScope(stores, focusDay ? { kind: 'day', date: focusDay } : null),
    [stores, focusDay]
  );

  const selectDay = (date: string) => {
    setFocusRoute(null);
    setFocusDay((current) => (current === date ? null : date));
  };

  const showOnMap = (route: RouteFocus & { zoneId: string }) => {
    setOpenDay(null);
    setMapZoneId(route.zoneId);
    setFocusDay(route.date);
    setFocusRoute({
      routeId: route.routeId,
      routeCode: route.routeCode,
      date: route.date,
    });
    mapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const [exporting, setExporting] = useState(false);
  const download = async () => {
    if (!kpis || exporting) return;
    setExporting(true);
    try {
      const workbook = await buildStoreVisitsWorkbook({
        stops: stores,
        kpis,
        days,
        materials,
        insights,
        range: filters.range,
        zoneNames: zones
          .filter((z) => zoneIds.includes(z.id))
          .map((z) => z.name),
        distributorNames: distributors
          .filter((d) => distributorIds.includes(d.id))
          .map((d) => d.tradeName),
        timeZone: userTimeZone,
        t: (key, options) => t(key, options) as string,
      });
      const url = window.URL.createObjectURL(await workbookToBlob(workbook));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = storeVisitsFileName(filters.range);
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => window.URL.revokeObjectURL(url), REVOKE_DELAY_MS);
    } catch (err) {
      console.error(err);
      message.error(t(`${I18N_BASE}.export.error`), ERROR_TOAST_SECONDS);
    } finally {
      setExporting(false);
    }
  };

  if (scope.loading) {
    return (
      <Layout title="">
        <Card loading />
      </Layout>
    );
  }

  if (scope.blockedReason) {
    return (
      <Layout title="">
        <RouteReportBlockedState reason={scope.blockedReason} />
      </Layout>
    );
  }

  const emptyRange = !loading && summary !== null && stores.length === 0;
  const dayFooter = bestWorst && (
    <div className="store-visits-day-footer">
      <span>
        {t(`${I18N_BASE}.day_chart.worst`)}{' '}
        <strong>
          {`${dayHeaderLabel(bestWorst.worst.date, language)} · ${formatDecimal(
            bestWorst.worst.compliancePct,
            language
          )}%`}
        </strong>
      </span>
      <span>
        {t(`${I18N_BASE}.day_chart.best`)}{' '}
        <strong>
          {`${dayHeaderLabel(bestWorst.best.date, language)} · ${formatDecimal(
            bestWorst.best.compliancePct,
            language
          )}%`}
        </strong>
      </span>
    </div>
  );

  return (
    <Layout title="">
      <div
        className="visit-history store-visits"
        style={{ ...themeVars, ...visitPaletteVars }}
      >
        <VisitHistoryHeader
          i18nBase={`${I18N_BASE}.header`}
          canDownload={!!kpis && stores.length > 0}
          exporting={exporting}
          onDownload={download}
        />

        <VisitHistoryFilters
          range={filters.range}
          onRangeChange={(range) => updateFilters({ ...filters, range })}
          zones={zones}
          zoneIds={zoneIds}
          onZoneChange={(nextZoneIds) =>
            updateFilters({
              ...filters,
              zoneIds: nextZoneIds,
              distributorIds: pruneDistributorIds(
                scope.distributors,
                nextZoneIds,
                filters.distributorIds
              ),
            })
          }
          distributors={distributors}
          distributorIds={distributorIds}
          onDistributorChange={(ids) =>
            updateFilters({ ...filters, distributorIds: ids })
          }
          singleDistributor={scope.singleDistributor}
          loading={loading}
          refreshing={refreshing}
          onRefresh={refresh}
          lastUpdatedAt={lastUpdatedAt}
          userTimeZone={userTimeZone}
        />

        {error ? (
          <VisitHistoryErrorState onRetry={refresh} />
        ) : emptyRange ? (
          <VisitHistoryEmptyRange />
        ) : (
          <>
            <StoreVisitsKpis
              kpis={loading ? null : kpis}
              observations={
                insights?.progress.complete ? insights.observations.total : null
              }
            />

            {loading || !summary ? (
              <Card loading />
            ) : (
              <>
                <div ref={mapRef}>
                  <StoreVisitsMap
                    stops={stores}
                    zoneId={mapZoneId}
                    onZoneChange={(id) => {
                      setMapZoneId(id);
                      setFocusRoute(null);
                    }}
                    focusDay={focusDay}
                    focusRoute={focusRoute}
                    onClearFocus={() => {
                      setFocusDay(null);
                      setFocusRoute(null);
                    }}
                    timeZone={userTimeZone}
                  />
                </div>

                <div className="visit-history-day-row">
                  <VisitsByDayChart
                    title={t(`${I18N_BASE}.day_chart.title`)}
                    points={dayPoints}
                    scope={dimScope}
                    onSelectDay={selectDay}
                    footer={dayFooter}
                  />
                  <StoreVisitsIncidents stops={stores} />
                </div>

                <div className="store-visits-row store-visits-row--wide">
                  <StoreVisitsMaterials summary={materials} />
                  <StoreVisitsCommunes summary={communes} />
                </div>

                <StoreVisitsCompetition
                  insights={insights}
                  loading={insightsState.loading}
                  failed={insightsState.failed}
                />

                <StoreVisitsDaySummary
                  days={days}
                  highlightDate={focusDay}
                  onOpenDay={setOpenDay}
                />

                <DayRoutesModal
                  date={openDay}
                  stops={stores}
                  timeZone={userTimeZone}
                  onClose={() => setOpenDay(null)}
                  onShowOnMap={showOnMap}
                />
              </>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

export default StoreVisits;
